// 일지·사진의 「어떤 행을 남길지」 — 인메모리·Supabase 가 한 벌을 쓴다.

import { todayKst } from "./view";
import type {
  DateRange,
  Project,
  ProjectLog,
  ProjectLogInput,
  ProjectPhoto,
  ProjectPhotoInsert,
} from "./types";

export const normalizeLogBody = (body: string): string => body.replace(/\r\n?/g, "\n").trim();

// 날씨는 앞뒤 공백을 털고 비면 null(`0133` rpc_project_log_add 의 nullif(btrim) 과 같다).
export const normalizeLogWeather = (weather: string | null | undefined): string | null => {
  const v = (weather ?? "").trim();
  return v === "" ? null : v;
};

export const buildLogRow = (args: {
  input: ProjectLogInput;
  projectId: string;
  orgId: string;
  authorId: string | null;
  at: string;
}): ProjectLog => ({
  id: args.input.id,
  org_id: args.orgId,
  project_id: args.projectId,
  log_date: args.input.log_date,
  kind: args.input.kind,
  body: normalizeLogBody(args.input.body),
  author_id: args.authorId,
  project_phase_id: args.input.project_phase_id ?? null,
  crew_count: args.input.crew_count ?? null,
  weather: normalizeLogWeather(args.input.weather),
  created_at: args.at,
  updated_at: args.at,
});

export const buildPhotoRow = (args: {
  input: ProjectPhotoInsert;
  projectId: string;
  orgId: string;
  uploadedBy: string | null;
  at: string;
}): ProjectPhoto => ({
  id: args.input.id,
  org_id: args.orgId,
  project_id: args.projectId,
  project_phase_id: args.input.project_phase_id,
  log_date: args.input.log_date,
  storage_path: args.input.storage_path,
  mime_type: args.input.mime_type,
  size_bytes: args.input.size_bytes,
  caption: args.input.caption.trim(),
  uploaded_by: args.uploadedBy,
  created_at: args.at,
  updated_at: args.at,
});

// 기록 가능한 날짜 — 착공 시각의 KST 날짜 ~ 오늘(KST). 착공 전이면 min 이 없다.
export const logDateBounds = (
  project: Pick<Project, "started_at">,
  today: string
): { min: string | null; max: string } => ({
  min: project.started_at === null ? null : todayKst(new Date(project.started_at)),
  max: today,
});

// 목록 범위(YYYY-MM-DD, 양끝 포함) — Supabase 의 gte/lte 와 같다.
export const inDateRange = (date: string, range: DateRange = {}): boolean =>
  (range.from === undefined || date >= range.from) && (range.to === undefined || date <= range.to);

// 목록 순서 — 날짜 최근 먼저, 같은 날은 쓴 순서.
export const compareRecords = (
  a: Pick<ProjectLog, "id" | "log_date" | "created_at">,
  b: Pick<ProjectLog, "id" | "log_date" | "created_at">
): number =>
  b.log_date.localeCompare(a.log_date) || a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);
